import {
  getDocumentControllerGetMyAccessQueryKey,
  getDocumentControllerGetValidationReviewQueryKey,
  getDocumentControllerListSubmitsQueryKey,
  useDocumentControllerGetMyAccess,
  useDocumentControllerSubmit,
} from '@app/api-client';
import type { DocumentDto } from '@app/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import {
  KEY_PUBLICATION_PENDING,
  KEY_PUBLICATION_REJECTED,
} from '../../publication/hooks/usePublicationLists.js';

type Props = {
  document: DocumentDto;
  className?: string;
};

export function DocumentSubmitButton({ document, className }: Props) {
  const qc = useQueryClient();
  const docId = document.id;

  const { data: access, isLoading, isError } = useDocumentControllerGetMyAccess(docId);

  const submit = useDocumentControllerSubmit({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: getDocumentControllerListSubmitsQueryKey(docId) });
        void qc.invalidateQueries({ queryKey: getDocumentControllerGetMyAccessQueryKey(docId) });
        void qc.invalidateQueries({
          queryKey: getDocumentControllerGetValidationReviewQueryKey(docId),
        });
        void qc.invalidateQueries({ queryKey: KEY_PUBLICATION_PENDING });
        void qc.invalidateQueries({ queryKey: KEY_PUBLICATION_REJECTED });
      },
    },
  });

  if (isLoading || isError) return null;
  if (!access?.redaction) return null;

  const busy = submit.isPending;

  return (
    <button
      type="button"
      disabled={busy}
      title="Créer une soumission : cliché figé du document en l’état"
      onClick={() => {
        if (
          !confirm(
            'Soumettre ce document ? Une copie figée (contenu + périmètres) sera enregistrée dans l’historique des soumissions.',
          )
        ) {
          return;
        }
        submit.mutate(
          { id: docId },
          {
            onError: () => {
              alert('Impossible de soumettre (vérifiez votre connexion ou vos droits).');
            },
          },
        );
      }}
      className={
        className ??
        'flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60'
      }
    >
      <Send className="h-4 w-4 text-emerald-600" />
      {busy ? 'Soumission…' : 'Soumettre'}
    </button>
  );
}
